import React, { useId, useRef } from 'react';
import { useGetClassesQuery } from '../../store/api/localApi';
import type { PolicyAudience } from '../../types';
import { CLASS_REMOVED, EVERYONE_AT_LOCATION } from './audienceLabel';

interface Props {
  locationId: string;
  /**
   * The audience being edited. `null` means "Chosen classes" with nothing
   * ticked: not a valid audience, so the parent form must disable its submit.
   */
  value: PolicyAudience | null;
  onChange: (next: PolicyAudience | null) => void;
  disabled?: boolean;
}

/**
 * The "Send to" picker: everyone at the location, or chosen classes.
 *
 * Never emits an empty class list — the server refuses one, and an empty list
 * reading as "nobody" would be a silent way to publish to no family. Instead it
 * emits `null` and says "Pick at least one class".
 *
 * Lists the location's ACTIVE classes alphabetically. A class the audience
 * already names but which is closed keeps its name; one that no longer exists
 * reads "(class removed)". Both stay ticked until the admin unticks them, so
 * re-saving never silently drops or keeps a class the admin cannot see.
 */
const PolicyAudienceControl: React.FC<Props> = ({ locationId, value, onChange, disabled }) => {
  const { data: classes, isLoading, isError } = useGetClassesQuery(locationId, { skip: !locationId });
  // The page can hold two of these at once (a publish panel and the Change
  // dialog); a shared radio name would make them one group.
  const radioName = useId();
  const isClasses = value === null || value.scope === 'CLASSES';
  const chosen = value?.scope === 'CLASSES' ? value.classIds : [];

  // Switching to everyone and back should not throw away the admin's ticks.
  const lastChosen = useRef<string[]>(chosen);
  if (chosen.length > 0) lastChosen.current = chosen;

  const emitClasses = (ids: string[]) =>
    onChange(ids.length > 0 ? { scope: 'CLASSES', classIds: ids } : null);

  const toggle = (classId: string, checked: boolean) =>
    emitClasses(checked ? [...chosen, classId] : chosen.filter((id) => id !== classId));

  const byId = new Map((classes ?? []).map((c) => [c.id, c]));
  const active = (classes ?? [])
    .filter((c) => c.isActive)
    .sort((a, b) => a.name.localeCompare(b.name));
  const activeIds = new Set(active.map((c) => c.id));
  const rows: { id: string; label: string }[] = [
    ...active.map((c) => ({ id: c.id, label: c.name })),
    // Ticked but not offered as active: closed (named) or gone (removed).
    ...chosen
      .filter((id) => !activeIds.has(id))
      .map((id) => ({ id, label: byId.get(id)?.name ?? CLASS_REMOVED })),
  ];

  return (
    <fieldset data-testid="policy-audience-control" disabled={disabled}>
      <legend className="block text-sm font-medium text-gray-700">Send to</legend>
      <div className="mt-1 space-y-1">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="radio"
            name={radioName}
            data-testid="policy-audience-scope-location"
            checked={!isClasses}
            onChange={() => onChange(EVERYONE_AT_LOCATION)}
            className="h-4 w-4 border-gray-300 text-primary-600"
          />
          Everyone at this location
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="radio"
            name={radioName}
            data-testid="policy-audience-scope-classes"
            checked={isClasses}
            onChange={() => emitClasses(lastChosen.current)}
            className="h-4 w-4 border-gray-300 text-primary-600"
          />
          Chosen classes
        </label>
      </div>

      {isClasses && (
        <div className="ml-6 mt-2 space-y-1">
          {isLoading && !classes ? (
            <p data-testid="policy-audience-classes-loading" className="text-xs text-gray-500">
              Loading classes…
            </p>
          ) : isError && !classes ? (
            // Without the list a ticked id cannot be told apart from a removed
            // class, so show nothing rather than a misleading "(class removed)".
            <p data-testid="policy-audience-classes-error" className="text-xs text-red-600">
              Could not load classes.
            </p>
          ) : rows.length === 0 ? (
            <p data-testid="policy-audience-no-classes" className="text-xs text-gray-500">
              This location has no classes yet.
            </p>
          ) : (
            rows.map((row) => (
              <label key={row.id} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  data-testid={`policy-audience-class-${row.id}`}
                  checked={chosen.includes(row.id)}
                  onChange={(e) => toggle(row.id, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                {row.label}
              </label>
            ))
          )}
          {value === null && (
            <p data-testid="policy-audience-invalid" className="text-xs text-red-600">
              Pick at least one class
            </p>
          )}
        </div>
      )}
    </fieldset>
  );
};

export default PolicyAudienceControl;
